import * as THREE from 'three';
import { createStarfield, theme } from '../visual';
import { PlayerShip } from '../gameplay/ships';
import type { FactionSelection } from '../gameplay/factions';

export class FactionSelectScene {
  readonly root = new THREE.Scene();
  readonly camera: THREE.PerspectiveCamera;
  private readonly pivot = new THREE.Group();
  private ship: PlayerShip | null = null;

  constructor(aspect: number) {
    this.root.background = new THREE.Color(theme.background);

    this.camera = new THREE.PerspectiveCamera(38, aspect, 0.1, 4000);
    this.camera.position.set(6.8, 2.4, 7.8);
    this.camera.lookAt(0, 0.4, 0);

    const stars = createStarfield({
      starCount: 900,
      dustCount: 2200,
      nebulaCount: 80
    });
    this.root.add(stars);
    this.setupLighting();

    this.pivot.position.set(0, 0, 0);
    this.root.add(this.pivot);
  }

  setShip(selection: FactionSelection): void {
    if (this.ship) {
      this.pivot.remove(this.ship.object3D);
      disposeObject(this.ship.object3D);
    }
    this.ship = new PlayerShip({ selection });
    this.ship.setThrust(0.35);
    this.pivot.add(this.ship.object3D);
  }

  update(dt: number): void {
    this.pivot.rotation.y += dt * 0.32;
    this.ship?.update(dt);
  }

  setAspect(aspect: number): void {
    this.camera.aspect = aspect;
    this.camera.updateProjectionMatrix();
  }

  dispose(): void {
    if (this.ship) {
      this.pivot.remove(this.ship.object3D);
      disposeObject(this.ship.object3D);
      this.ship = null;
    }
  }

  private setupLighting(): void {
    const { ambient, key, rim, fill } = theme.lighting;
    const amb = new THREE.AmbientLight(ambient.color, ambient.intensity + 0.1);
    const keyLight = new THREE.DirectionalLight(key.color, key.intensity);
    keyLight.position.set(key.position.x, key.position.y, key.position.z);
    const rimLight = new THREE.DirectionalLight(rim.color, rim.intensity);
    rimLight.position.set(rim.position.x, rim.position.y, rim.position.z);
    const fillLight = new THREE.DirectionalLight(fill.color, fill.intensity);
    fillLight.position.set(fill.position.x, fill.position.y, fill.position.z);
    this.root.add(amb, keyLight, rimLight, fillLight);
  }
}

function disposeObject(obj: THREE.Object3D): void {
  obj.traverse((child) => {
    if (child instanceof THREE.Mesh) {
      child.geometry?.dispose();
      const mat = child.material;
      if (Array.isArray(mat)) mat.forEach((m) => m.dispose());
      else mat?.dispose();
    }
  });
}
